import React, { useState, useEffect } from 'react';
import {
  View,
  ScrollView,
  ActivityIndicator,
  Alert,
} from 'react-native';
import { isWeb } from '../util/platform';
import CustomText from '../components/CustomText';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AppButton from '../components/AppButton';
import Util from '../util/util.js';
import { formatMediumDate } from '../util/format';
import { useResponsiveStyles } from '../hooks';
import AppStyles from '../AppStyles';
import { useTranslation } from '../i18n';

const DrawingDetails = ({ navigation, route }) => {
  const { t } = useTranslation('drawings');

  const { drawingId, organization, admin, userAdmin, orgAdmin } = route.params || {};
  const { containerStyle } = useResponsiveStyles();

  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [drawing, setDrawing] = useState(null);
  const [participants, setParticipants] = useState([]);

  useEffect(() => {
    loadDrawing();
  }, [drawingId]);

  const loadDrawing = async () => {
    try {
      setLoading(true);
      const response = await Util.get('Resty', `/items/drawings/${drawingId}`);
      if (response && !response.hasError) {
        setDrawing(response.drawing);
        setParticipants(response.participants || []);
      }
    } catch (error) {
      console.log('Error loading drawing:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return t('details.notAvailable');
    // Use UTC to avoid timezone day-shift issues
    return formatMediumDate(timestamp, { timeZone: 'UTC' });
  };

  const getTimeRemaining = () => {
    if (!drawing?.endDate || drawing.status !== 'active') return null;

    // Use UTC dates at midnight to match displayed dates
    const now = new Date();
    const todayUTC = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());

    const endDate = new Date(drawing.endDate);
    const endUTC = Date.UTC(endDate.getUTCFullYear(), endDate.getUTCMonth(), endDate.getUTCDate());

    const diff = endUTC - todayUTC;
    if (diff <= 0) return t('drawings:countdown.endingSoon');

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    if (days > 0) return t('drawings:countdown.daysRemaining', { count: days });
    return t('drawings:countdown.endingSoon');
  };

  const handleEndDrawing = () => {
    if (isWeb()) {
      if (window.confirm(t('details.endConfirm'))) {
        endDrawing();
      }
    } else {
      Alert.alert(
        t('details.endTitle'),
        t('details.endConfirm'),
        [
          { text: t('common:actions.cancel'), style: 'cancel' },
          { text: t('details.endConfirmButton'), style: 'destructive', onPress: endDrawing },
        ]
      );
    }
  };

  const showMessage = (title, message) => {
    if (isWeb()) {
      window.alert(`${title}\n\n${message}`);
    } else {
      Alert.alert(title, message, [{ text: 'OK' }]);
    }
  };

  const endDrawing = async () => {
    try {
      setActionLoading(true);
      const response = await Util.post('Resty', `/items/drawings/${drawingId}/end`, {});
      if (response && !response.hasError) {
        showMessage(
          t('details.endedTitle'),
          response.winner
            ? `Winner: ${response.winner.userId}`
            : t('details.noParticipantsAlert')
        );
        loadDrawing();
      } else {
        showMessage('Error', response.error || t('details.endFailed'));
      }
    } catch (error) {
      console.log('Error ending drawing:', error);
      showMessage('Error', t('details.endFailed'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelDrawing = () => {
    if (isWeb()) {
      if (window.confirm(t('details.cancelConfirm'))) {
        cancelDrawing();
      }
    } else {
      Alert.alert(
        t('details.cancelTitle'),
        t('details.cancelConfirm'),
        [
          { text: t('common:answer.no'), style: 'cancel' },
          { text: t('details.cancelConfirmButton'), style: 'destructive', onPress: cancelDrawing },
        ]
      );
    }
  };

  const cancelDrawing = async () => {
    try {
      setActionLoading(true);
      const response = await Util.post('Resty', `/items/drawings/${drawingId}/cancel`, {});
      if (response && !response.hasError && !response.error) {
        showMessage(t('details.successTitle'), t('details.cancelled'));
        navigation.goBack();
      } else {
        showMessage('Error', response?.error || t('details.cancelFailed'));
      }
    } catch (error) {
      console.error('Error cancelling drawing:', error);
      const errorMessage = error?.message || error?.toString() || t('details.cancelFailed');
      showMessage('Error', errorMessage);
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return AppStyles.colorSet.success;
      case 'ended':
        return AppStyles.colorSet.info;
      case 'cancelled':
        return AppStyles.colorSet.error;
      default:
        return AppStyles.colorSet.textSecondary;
    }
  };

  const timeRemaining = drawing ? getTimeRemaining() : null;

  if (loading) {
    return (
      <View style={[containerStyle, styles.container]}>
        <Header organization={organization} admin={admin} userAdmin={userAdmin} type="pop" />
        <View style={styles.loaderContainer}>
          <ActivityIndicator size="large" color={AppStyles.colorSet.brandPrimary} />
        </View>
        <Footer organization={organization} admin={admin} userAdmin={userAdmin} orgAdmin={orgAdmin} />
      </View>
    );
  }

  if (!drawing) {
    return (
      <View style={[containerStyle, styles.container]}>
        <Header organization={organization} admin={admin} userAdmin={userAdmin} type="pop" />
        <View style={styles.errorContainer}>
          <CustomText style={styles.errorText}>{t('details.notFound')}</CustomText>
        </View>
        <Footer organization={organization} admin={admin} userAdmin={userAdmin} orgAdmin={orgAdmin} />
      </View>
    );
  }

  return (
    <View style={[containerStyle, styles.container]}>
      <Header organization={organization} admin={admin} userAdmin={userAdmin} type="pop" />
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.content}
      >
        {/* Header with status */}
        <View style={styles.headerSection}>
          <View style={styles.headerLeft}>
            <CustomText style={styles.drawingName}>{drawing.name}</CustomText>
            {timeRemaining && (
              <View style={styles.timeRemainingBadge}>
                <CustomText style={styles.timeRemainingText}>{timeRemaining}</CustomText>
              </View>
            )}
          </View>
          <View
            style={[
              styles.statusBadge,
              { backgroundColor: getStatusColor(drawing.status) },
            ]}
          >
            <CustomText style={styles.statusText}>
              {drawing.status.toUpperCase()}
            </CustomText>
          </View>
        </View>

        {drawing.description && (
          <CustomText style={styles.description}>{drawing.description}</CustomText>
        )}

        {/* Details Section */}
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <CustomText style={styles.sectionIcon}>📅</CustomText>
            <CustomText style={styles.sectionTitle}>{t('details.details')}</CustomText>
          </View>
          <View style={styles.detailCard}>
            <View style={styles.detailRow}>
              <CustomText style={styles.detailLabel}>{t('details.startDate')}</CustomText>
              <CustomText style={styles.detailValue}>
                {formatDate(drawing.startDate)}
              </CustomText>
            </View>
            <View style={styles.detailRow}>
              <CustomText style={styles.detailLabel}>{t('details.endDate')}</CustomText>
              <CustomText style={styles.detailValue}>
                {formatDate(drawing.endDate)}
              </CustomText>
            </View>
            <View style={styles.detailRow}>
              <CustomText style={styles.detailLabel}>{t('details.createdBy')}</CustomText>
              <CustomText style={styles.detailValue}>
                {drawing.createdBy}
              </CustomText>
            </View>
            <View style={[styles.detailRow, styles.detailRowLast]}>
              <CustomText style={styles.detailLabel}>{t('details.participants')}</CustomText>
              <CustomText style={[styles.detailValue, styles.participantCount]}>
                {drawing.totalParticipants || participants.length}
              </CustomText>
            </View>
          </View>
        </View>

        {/* Organizations Section */}
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <CustomText style={styles.sectionIcon}>🏢</CustomText>
            <CustomText style={styles.sectionTitle}>{t('details.eligibleOrganizations')}</CustomText>
          </View>
          <View style={styles.orgsContainer}>
            {drawing.organizations?.map((org, index) => (
              <View key={index} style={styles.orgChip}>
                <CustomText style={styles.orgChipText}>{org}</CustomText>
              </View>
            ))}
          </View>
        </View>

        {/* Prize Section */}
        {drawing.prizeInfo && (drawing.prizeInfo.name || drawing.prizeInfo.value) && (
          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <CustomText style={styles.sectionIcon}>🎁</CustomText>
              <CustomText style={styles.sectionTitle}>{t('details.prize')}</CustomText>
            </View>
            <View style={styles.prizeCard}>
              {drawing.prizeInfo.name && (
                <CustomText style={styles.prizeName}>{drawing.prizeInfo.name}</CustomText>
              )}
              {drawing.prizeInfo.value && (
                <CustomText style={styles.prizeValue}>
                  {drawing.prizeInfo.value.startsWith('$') ? drawing.prizeInfo.value : `$${drawing.prizeInfo.value}`}
                </CustomText>
              )}
              {drawing.prizeInfo.description && (
                <CustomText style={styles.prizeDescription}>
                  {drawing.prizeInfo.description}
                </CustomText>
              )}
            </View>
          </View>
        )}

        {/* Winner Section */}
        {drawing.status === 'ended' && drawing.winnerId && (
          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <CustomText style={styles.sectionIcon}>🏆</CustomText>
              <CustomText style={styles.sectionTitle}>{t('details.winner')}</CustomText>
            </View>
            <View style={styles.winnerCard}>
              <CustomText style={styles.winnerEmail}>{drawing.winnerId}</CustomText>
              <CustomText style={styles.winnerLabel}>
                {t('details.selectedOn', { date: formatDate(drawing.endedAt) })}
              </CustomText>
            </View>
          </View>
        )}

        {/* Participants Section */}
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <CustomText style={styles.sectionIcon}>👥</CustomText>
            <CustomText style={styles.sectionTitle}>
              {t('details.participants')}{drawing.totalParticipants > 0 ? ` (${drawing.totalParticipants})` : ''}
            </CustomText>
          </View>
          {(() => {
            // Group entries by user and count
            const userEntries = participants.reduce((acc, p) => {
              if (!acc[p.userId]) {
                acc[p.userId] = { userId: p.userId, organization: p.organization, entries: 0 };
              }
              acc[p.userId].entries += 1;
              return acc;
            }, {});
            const uniqueParticipants = Object.values(userEntries);

            return uniqueParticipants.length > 0 ? (
              <View style={styles.participantsList}>
                {uniqueParticipants.slice(0, 20).map((p, index) => (
                  <View key={index} style={styles.participantRow}>
                    <View style={styles.participantInfo}>
                      <CustomText style={styles.participantEmail}>{p.userId}</CustomText>
                      <CustomText style={styles.participantOrg}>{p.organization}</CustomText>
                    </View>
                    <CustomText style={styles.participantEntries}>
                      {t('details.entryCount', { count: p.entries })}
                    </CustomText>
                  </View>
                ))}
                {uniqueParticipants.length > 20 && (
                  <CustomText style={styles.moreText}>
                    {t('details.moreParticipants', { count: uniqueParticipants.length - 20 })}
                  </CustomText>
                )}
              </View>
            ) : (
              <View style={styles.emptyParticipants}>
                <CustomText style={styles.emptyParticipantsText}>
                  {t('details.noParticipants')}
                </CustomText>
                <CustomText style={styles.emptyParticipantsSubtext}>
                  {t('details.noParticipantsHint')}
                </CustomText>
              </View>
            );
          })()}
        </View>

        {/* Action Buttons */}
        {drawing.status === 'active' && (
          <View style={styles.actionsSection}>
            <AppButton
              title={actionLoading ? t('details.processing') : t('details.endAndSelect')}
              onPress={handleEndDrawing}
              isEnabled={!actionLoading}
              buttonStyle={styles.endButton}
            />
            <AppButton
              title={t('details.cancelTitle')}
              onPress={handleCancelDrawing}
              isEnabled={!actionLoading}
              buttonStyle={styles.cancelButton}
              textStyle={styles.cancelButtonText}
            />
          </View>
        )}
      </ScrollView>
      <Footer organization={organization} admin={admin} userAdmin={userAdmin} orgAdmin={orgAdmin} />
    </View>
  );
};

const styles = {
  container: {
    flex: 1,
    backgroundColor: AppStyles.colorSet.inputBackground,
  },
  scrollView: {
    flex: 1,
    width: '100%',
  },
  content: {
    padding: 20,
    paddingBottom: 100,
    flexGrow: 1,
    alignItems: 'stretch',
    maxWidth: 600,
    alignSelf: 'center',
    width: '100%',
  },
  loaderContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorText: {
    fontSize: 16,
    color: AppStyles.colorSet.error,
  },
  headerSection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 16,
  },
  headerLeft: {
    flex: 1,
    marginRight: 12,
  },
  drawingName: {
    fontSize: 28,
    fontWeight: 'bold',
    color: AppStyles.colorSet.textPrimary,
    marginBottom: 8,
  },
  timeRemainingBadge: {
    backgroundColor: AppStyles.colorSet.info + '20',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
    alignSelf: 'flex-start',
  },
  timeRemainingText: {
    fontSize: 13,
    fontWeight: '500',
    color: AppStyles.colorSet.info,
  },
  statusBadge: {
    paddingHorizontal: 14,
    paddingVertical: 6,
    borderRadius: 16,
  },
  statusText: {
    fontSize: 12,
    fontWeight: '700',
    color: AppStyles.colorSet.whiteColor,
    letterSpacing: 0.5,
  },
  description: {
    fontSize: 16,
    color: AppStyles.colorSet.textSecondary,
    marginBottom: 24,
    lineHeight: 24,
  },
  section: {
    marginBottom: 24,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  sectionIcon: {
    fontSize: 18,
    marginRight: 8,
  },
  sectionTitle: {
    fontSize: 17,
    fontWeight: '600',
    color: AppStyles.colorSet.textPrimary,
  },
  detailCard: {
    backgroundColor: AppStyles.colorSet.whiteColor,
    borderRadius: 12,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 3,
    elevation: 1,
  },
  detailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: AppStyles.borderSet.default,
  },
  detailRowLast: {
    borderBottomWidth: 0,
  },
  detailLabel: {
    fontSize: 14,
    color: AppStyles.colorSet.textSecondary,
  },
  detailValue: {
    fontSize: 14,
    fontWeight: '500',
    color: AppStyles.colorSet.textPrimary,
  },
  participantCount: {
    fontSize: 16,
    fontWeight: '700',
    color: AppStyles.colorSet.success,
  },
  orgsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  orgChip: {
    backgroundColor: AppStyles.colorSet.success,
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 20,
    marginRight: 8,
    marginBottom: 8,
  },
  orgChipText: {
    fontSize: 14,
    fontWeight: '500',
    color: AppStyles.colorSet.whiteColor,
  },
  prizeCard: {
    backgroundColor: AppStyles.colorSet.whiteColor,
    borderRadius: 12,
    padding: 20,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 3,
    elevation: 1,
  },
  prizeName: {
    fontSize: 18,
    fontWeight: '600',
    color: AppStyles.colorSet.textPrimary,
    marginBottom: 4,
  },
  prizeValue: {
    fontSize: 28,
    fontWeight: '700',
    color: AppStyles.colorSet.success,
    marginBottom: 8,
  },
  prizeDescription: {
    fontSize: 14,
    color: AppStyles.colorSet.textSecondary,
    textAlign: 'center',
    marginTop: 4,
  },
  winnerCard: {
    backgroundColor: AppStyles.colorSet.successLight || '#E8F5E9',
    borderRadius: 12,
    padding: 20,
    alignItems: 'center',
    borderWidth: 2,
    borderColor: AppStyles.colorSet.success,
  },
  winnerEmail: {
    fontSize: 18,
    fontWeight: '600',
    color: AppStyles.colorSet.success,
  },
  winnerLabel: {
    fontSize: 14,
    color: AppStyles.colorSet.textSecondary,
    marginTop: 4,
  },
  participantsList: {
    backgroundColor: AppStyles.colorSet.whiteColor,
    borderRadius: 12,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 3,
    elevation: 1,
  },
  participantRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: AppStyles.borderSet.default,
  },
  participantInfo: {
    flex: 1,
  },
  participantEmail: {
    fontSize: 14,
    color: AppStyles.colorSet.textPrimary,
  },
  participantOrg: {
    fontSize: 12,
    color: AppStyles.colorSet.textSecondary,
    marginTop: 2,
  },
  participantEntries: {
    fontSize: 13,
    color: AppStyles.colorSet.textSecondary,
    marginLeft: 12,
  },
  moreText: {
    fontSize: 14,
    color: AppStyles.colorSet.textSecondary,
    textAlign: 'center',
    marginTop: 12,
  },
  emptyParticipants: {
    backgroundColor: AppStyles.colorSet.whiteColor,
    borderRadius: 12,
    padding: 24,
    alignItems: 'center',
  },
  emptyParticipantsText: {
    fontSize: 16,
    fontWeight: '500',
    color: AppStyles.colorSet.textSecondary,
    marginBottom: 8,
  },
  emptyParticipantsSubtext: {
    fontSize: 14,
    color: AppStyles.colorSet.textSecondary,
    textAlign: 'center',
    opacity: 0.7,
  },
  actionsSection: {
    marginTop: 12,
    paddingTop: 20,
    borderTopWidth: 1,
    borderTopColor: AppStyles.borderSet.default,
  },
  endButton: {
    marginBottom: 4,
    width: '100%',
  },
  cancelButton: {
    backgroundColor: 'transparent',
    borderWidth: 0,
    width: '100%',
  },
  cancelButtonText: {
    color: AppStyles.colorSet.error,
    fontWeight: '500',
  },
};

export default DrawingDetails;
